import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  AUDIENCE_STORAGE_KEY,
  audienceDefinitions,
  audienceFromSearch,
  isPresenterAudience,
  resolveInitialAudience,
  searchWithAudience,
  searchWithoutInvalidAudience,
  type AudienceDefinition,
  type PresenterAudience,
} from "./audience";

type AudienceContextValue = {
  announcement: string;
  audience: PresenterAudience;
  definition: AudienceDefinition;
  setAudience: (audience: PresenterAudience) => void;
};

const AudienceContext = createContext<AudienceContextValue | null>(null);

function readStoredAudience() {
  try {
    return window.localStorage.getItem(AUDIENCE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredAudience(audience: PresenterAudience) {
  try {
    window.localStorage.setItem(AUDIENCE_STORAGE_KEY, audience);
  } catch {
    // Session state still works when local storage is unavailable.
  }
}

function replaceAudienceUrl(audience: PresenterAudience) {
  const search = searchWithAudience(window.location.search, audience);
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${search}${window.location.hash}`,
  );
}

function cleanInvalidAudienceUrl() {
  if (audienceFromSearch(window.location.search) !== "invalid") return;
  const search = searchWithoutInvalidAudience(window.location.search);
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${search}${window.location.hash}`,
  );
}

export function AudienceProvider({ children }: { children: ReactNode }) {
  const [audience, updateAudience] = useState<PresenterAudience>(() =>
    resolveInitialAudience(window.location.search, readStoredAudience()),
  );
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    document.documentElement.dataset.presentationAudience = audience;
    return () => {
      delete document.documentElement.dataset.presentationAudience;
    };
  }, [audience]);

  useEffect(() => {
    const urlAudience = audienceFromSearch(window.location.search);
    const storedAudience = readStoredAudience();
    if (urlAudience === "invalid") {
      writeStoredAudience("telecom-admin");
      cleanInvalidAudienceUrl();
    } else if (urlAudience) {
      writeStoredAudience(urlAudience);
    } else if (isPresenterAudience(storedAudience)) {
      replaceAudienceUrl(storedAudience);
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const urlAudience = audienceFromSearch(window.location.search);
      const stored = readStoredAudience();
      const nextAudience = urlAudience === "invalid"
        ? "telecom-admin"
        : urlAudience ?? (isPresenterAudience(stored) ? stored : "telecom-admin");
      updateAudience(nextAudience);
      if (urlAudience === "invalid") {
        writeStoredAudience("telecom-admin");
        cleanInvalidAudienceUrl();
      } else if (urlAudience) {
        writeStoredAudience(urlAudience);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const setAudience = useCallback((nextAudience: PresenterAudience) => {
    updateAudience(nextAudience);
    writeStoredAudience(nextAudience);
    replaceAudienceUrl(nextAudience);
    setAnnouncement(`Audience language changed to ${audienceDefinitions[nextAudience].label}.`);
  }, []);

  const value = useMemo<AudienceContextValue>(() => ({
    announcement,
    audience,
    definition: audienceDefinitions[audience],
    setAudience,
  }), [announcement, audience, setAudience]);

  return <AudienceContext.Provider value={value}>{children}</AudienceContext.Provider>;
}

export function useAudience() {
  const context = useContext(AudienceContext);
  if (!context) throw new Error("useAudience must be used inside AudienceProvider");
  return context;
}

